import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses
} from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import { styled, SxProps, Theme } from '@mui/material/styles';
import AssistantCitation from 'ai-assistant/citations/assistant-citation';
import CitationExcerpt from 'ai-assistant/citations/_views/citation-excerpt';
import CitationSourceKindIcon from 'ai-assistant/citations/_views/citation-source-kind-icon';
import CitationSourceLink from 'ai-assistant/citations/_views/citation-source-link';
import React from 'react';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));
const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '1rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
  {
    transform: 'rotate(90deg)',
  },
  [`& .${accordionSummaryClasses.content}`]: {
    justifyContent: 'space-between',
    marginLeft: theme.spacing(1)
  },
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  }),
}));
const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));
const Title = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  fontWeight: theme.typography.fontWeightMedium,
})) as typeof Typography;

/**
 * The DOM id of one citation panel, so a marker in the answer can scroll to it.
 *
 * Scoped to the answer it belongs to. Ordinals are turn-local by design and a conversation
 * renders every answer into one document, so keying on the ordinal alone gave every answer
 * the same set of ids: clicking a marker scrolled to the first match in the document -
 * usually a panel in an older answer - and the duplicates were invalid HTML besides.
 */
export const citationPanelId = (scope: string, ordinal: number) =>
  `assistant-citation-${scope}-${ordinal}`;

/** The id of a panel's summary: the control a marker hands focus to when it opens the panel. */
export const citationSummaryId = (scope: string, ordinal: number) =>
  `${citationPanelId(scope, ordinal)}-summary`;

/** The id of a panel's region, the part that opens: what a marker says it controls. */
export const citationRegionId = (scope: string, ordinal: number) =>
  `${citationPanelId(scope, ordinal)}-region`;

/** The id of the row that holds several spans of one resource, named by its first ordinal. */
export const citationGroupId = (scope: string, firstOrdinal: number) =>
  `assistant-citation-${scope}-group-${firstOrdinal}`;

/**
 * The line under a citation's name: what kind of thing the source is (#5986), where in it the
 * text is, and whether OCR read it (#6019), as "File · Sheet: Rates 2026 · Read by OCR".
 *
 * Inside the summary, so all of it is part of the control's accessible name and a screen-reader
 * user hears the kind with the source. Nothing at all for a citation that says none of these,
 * which is every citation recorded before they existed: it reads exactly as it did.
 */
function CitationSourceDetail({ citation }: Readonly<{ citation: AssistantCitation }>) {
  const parts = [citation.sourceKindLabel, citation.locatorName, citation.textSourceName]
    .filter((part): part is string => Boolean(part));
  if (parts.length === 0) return null;
  return (
    <Typography
      component="span"
      variant="caption"
      data-testid="citation-source-detail"
      sx={{ color: 'text.secondary', display: 'block', overflowWrap: 'anywhere' }}
    >
      {parts.join(' · ')}
    </Typography>
  );
}

/**
 * Said above an excerpt that quotes a figure from pages OCR read some number on with low
 * confidence (#6019). The flag is per page, not per number, so it names the risk rather than the
 * figure.
 */
function UncertainFigureWarning({ citation }: Readonly<{ citation: AssistantCitation }>) {
  const confidence = citation.ocrConfidence === undefined ? '' : ` (${citation.ocrConfidence}% confidence)`;
  return (
    <Alert severity="warning" variant="outlined" sx={{ mb: 1, py: 0, typography: 'caption' }}>
      {`Figures here were read by OCR${confidence} and may be wrong. Check the original.`}
    </Alert>
  );
}

/** The spans of one resource, in the order the server numbered them. */
type CitationGroup = { key: string; citations: AssistantCitation[] };

/**
 * The citations grouped by the resource they quote (#5988). Each retrieved span is its own
 * citation, so two chunks of one document were two rows with the same name. Grouping is
 * presentation only: every span keeps the ordinal the server gave it.
 */
const groupByResource = (citations: AssistantCitation[]): CitationGroup[] => {
  const groups = new Map<string, CitationGroup>();
  for (const citation of citations) {
    const group = groups.get(citation.resourceKey);
    if (group) group.citations.push(citation);
    else groups.set(citation.resourceKey, { key: citation.resourceKey, citations: [citation] });
  }
  return [...groups.values()];
};

/** The ordinals, the kind's icon and the source's name: the start of a row's summary. */
function SourceName({ citation, ordinals }: Readonly<{ citation: AssistantCitation; ordinals: number[] }>) {
  return (
    <>
      {`${ordinals.join(', ')}. `}
      {citation.sourceKind ? (
        <Box component="span" sx={{ display: 'inline-flex', verticalAlign: '-0.15em', mr: 0.5 }}>
          <CitationSourceKindIcon kind={citation.sourceKind} />
        </Box>
      ) : null}
      {citation.sourceLabel}
    </>
  );
}

/** What an open span shows: its excerpt and a way to the source, or why the excerpt is gone. */
function CitationBody({ citation }: Readonly<{ citation: AssistantCitation }>) {
  if (citation.withheld) {
    /*
     * The panel still exists, because the answer text still carries the marker and a
     * marker that scrolls to nothing reads as a bug. What it says is why the excerpt
     * is not here - the answer was written when the reader could see this source and
     * the server re-checks that every time it serves the conversation back.
     */
    return (
      <Typography variant="body2" component="div" sx={{ fontStyle: 'italic' }}>
        This excerpt is no longer available to you. You could see this source when the
        answer was written; you cannot now.
      </Typography>
    );
  }
  return (
    <>
      {citation.quotesUncertainFigure ? <UncertainFigureWarning citation={citation} /> : null}
      <CitationExcerpt html={citation.text ?? ''} />
      <CitationSourceLink citation={citation} />
    </>
  );
}

/**
 * Where in its resource a span is, for its row inside a group: the page, the section, sheet or
 * slide, and whether OCR read it. A span that says none of these is numbered among the group's.
 */
const spanPlace = (citation: AssistantCitation, index: number) => {
  const parts = [
    citation.pageName ? `Page ${citation.pageName}` : undefined,
    citation.locatorName,
    citation.textSourceName,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : `Passage ${index + 1}`;
};

type CitationPanelProps = {
  citation: AssistantCitation;
  panelScope: string;
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  onOpened?: (ordinal: number) => void;
  /** What the summary says. A row of its own names the source; a span in a group, its place. */
  summary: React.ReactNode;
};

/** One span: a summary that names it, and its excerpt below once it is opened. */
function CitationPanel(props: Readonly<CitationPanelProps>) {
  const { citation, panelScope, expanded, onChange, onOpened, summary } = props;
  return (
    <Accordion
      id={citationPanelId(panelScope, citation.ordinal)}
      expanded={expanded}
      onChange={onChange}
      slotProps={{
        transition: {
          // A long conversation holds many answers, each with several excerpts of up to
          // 4,000 characters. Only an open panel keeps its excerpt in the document.
          unmountOnExit: true,
          onEntered: () => onOpened?.(citation.ordinal),
        },
      }}
    >
      <AccordionSummary
        id={citationSummaryId(panelScope, citation.ordinal)}
        aria-controls={citationRegionId(panelScope, citation.ordinal)}
      >
        {summary}
      </AccordionSummary>
      <AccordionDetails>
        <CitationBody citation={citation} />
      </AccordionDetails>
    </Accordion>
  );
}

/** A group the reader keeps open with none of its spans open, and what was open when they did. */
type OpenGroup = { key: string; ordinal?: number };

type CitationsProps = {
  className?: string;
  sx?: SxProps<Theme>;
  citations: AssistantCitation[];
  /** Distinguishes this answer's panel ids from every other answer's. */
  panelScope: string;
  /** The ordinal to show expanded, if any. Controlled by the answer's marker clicks. */
  expandedOrdinal?: number;
  onExpandedOrdinalChange?: (ordinal: number | undefined) => void;
  /** Called once a panel has finished opening, so the caller can bring all of it into view. */
  onOpened?: (ordinal: number) => void;
};

export default function Citations(props: Readonly<CitationsProps>) {
  const {
    className,
    sx,
    citations,
    panelScope,
    expandedOrdinal,
    onExpandedOrdinalChange,
    onOpened
  } = props;

  const [uncontrolledOrdinal, setUncontrolledOrdinal] = React.useState<number | undefined>(undefined);
  // A group stays open with none of its spans open only after the reader closes the span they
  // were reading. Keyed on what was open then, so a marker that opens anything else closes it.
  const [openGroup, setOpenGroup] = React.useState<OpenGroup | undefined>(undefined);
  const isControlled = onExpandedOrdinalChange !== undefined;
  const expanded = isControlled ? expandedOrdinal : uncontrolledOrdinal;
  const groups = groupByResource(citations);

  const select = (ordinal: number | undefined) => {
    if (isControlled) {
      onExpandedOrdinalChange(ordinal);
      return;
    }
    setUncontrolledOrdinal(ordinal);
  };

  const handleChange = (ordinal: number) => (_event: React.SyntheticEvent, isExpanded: boolean) =>
    select(isExpanded ? ordinal : undefined);

  const holdsExpanded = (group: CitationGroup) =>
    group.citations.some((citation) => citation.ordinal === expanded);

  const isGroupOpen = (group: CitationGroup) =>
    holdsExpanded(group) || (openGroup?.key === group.key && openGroup.ordinal === expanded);

  // Opening a group opens its first span, so one click shows a passage as a lone source's does.
  const handleGroupChange = (group: CitationGroup) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      select(group.citations[0].ordinal);
      return;
    }
    setOpenGroup(undefined);
    if (holdsExpanded(group)) select(undefined);
  };

  const handleSpanChange = (group: CitationGroup, ordinal: number) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded) {
        select(ordinal);
        return;
      }
      setOpenGroup({ key: group.key, ordinal: undefined });
      select(undefined);
    };

  return (
    <Box className={className} sx={sx}>
      {/*
        * A heading, so a screen-reader user can jump between an answer and its sources. Level 4:
        * the assistant panel's own title is the h3 every answer sits under. It counts resources,
        * which is what the rows are.
        */}
      <Typography variant="subtitle2" gutterBottom component="h4" sx={{ ml: 1 }}>
        {`Sources (${groups.length})`}
      </Typography>
      {groups.map((group) => {
        const [first] = group.citations;
        if (group.citations.length === 1) {
          return (
            <CitationPanel
              key={first.key}
              citation={first}
              panelScope={panelScope}
              expanded={expanded === first.ordinal}
              onChange={handleChange(first.ordinal)}
              onOpened={onOpened}
              summary={
                <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
                  <Title component="span" sx={{ display: 'block' }}>
                    <SourceName citation={first} ordinals={[first.ordinal]} />
                    {first.pageName ? (
                      <>
                        {' '}
                        {/* One unit, so a label such as "A-1" never breaks at its hyphen. */}
                        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{`(Page ${first.pageName})`}</Box>
                      </>
                    ) : null}
                  </Title>
                  {/* Keeps the name and the detail two words apart in the summary's accessible name. */}
                  {' '}
                  <CitationSourceDetail citation={first} />
                </Box>
              }
            />
          );
        }

        const groupId = citationGroupId(panelScope, first.ordinal);
        const shown = group.citations.find((citation) => citation.ordinal === expanded);
        return (
          <Accordion
            key={group.key}
            id={groupId}
            expanded={isGroupOpen(group)}
            onChange={handleGroupChange(group)}
            slotProps={{
              transition: {
                unmountOnExit: true,
                // A span opened with its group plays no transition of its own; the group's says
                // when all of it is on screen.
                onEntered: () => {
                  if (shown) onOpened?.(shown.ordinal);
                },
              },
            }}
          >
            <AccordionSummary id={`${groupId}-summary`} aria-controls={`${groupId}-region`}>
              <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
                <Title component="span" sx={{ display: 'block' }}>
                  <SourceName citation={first} ordinals={group.citations.map((citation) => citation.ordinal)} />
                </Title>
                {' '}
                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {[first.sourceKindLabel, `${group.citations.length} passages`].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 1 }}>
              {group.citations.map((citation, index) => (
                <CitationPanel
                  key={citation.key}
                  citation={citation}
                  panelScope={panelScope}
                  expanded={expanded === citation.ordinal}
                  onChange={handleSpanChange(group, citation.ordinal)}
                  onOpened={onOpened}
                  summary={
                    <Title component="span" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                      {`${citation.ordinal}. ${spanPlace(citation, index)}`}
                    </Title>
                  }
                />
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
